import React from "react";
import { Link } from "react-router-dom";
import galleryImage from "../assets/img/gallery.img/gallery.webp";
import zebraImg from "../assets/img/blinds.img/zebra_blinds.jpg";
import rollerImg from "../assets/img/blinds.img/ripple_blinds.jpg";
import verticalImg from "../assets/img/blinds.img/vertical_blinds.jpg";
import bambooImg from "../assets/img/blinds.img/bamboo_blinds.jpg";
import venetianImg from "../assets/img/blinds.img/venetian_blinds.jpg";
import romanImg from "../assets/img/blinds.img/roman_blinds.jpg";
import { useTranslation } from "language-maps/use-translation";
import "../assets/css/blinds.css";

const blindTypes = [
  {
    first: "ZEBRA",
    second: "Blinds",
    slug: "zebrablinds",
    image: zebraImg,
    tagline: "Perfect balance of light and privacy.",
    description:
      "Zebra blinds, also known as day and night blinds or dual-layer blinds, are a modern window covering that combines the features of both roller blinds and sheer curtains. They consist of alternating horizontal fabric stripes — one sheer (transparent) and one opaque (solid) — that move in tandem to control light and privacy.\nBy adjusting the position of the stripes, you can easily switch between light filtering and full privacy modes:",
    bullets: [
      "When the sheer and solid stripes align alternately, natural light softly filters through.",
      "When the solid stripes overlap, the blinds block out light for complete privacy.",
    ],
    extra:
      "Zebra blinds offer a sleek, contemporary look suitable for homes, offices, and commercial spaces. They are available in a variety of colors, textures, and fabric types to match different interior styles.",
    features: [
      "Dual-layer fabric for versatile light control",
      "Easy to clean and maintain",
      "Ideal for living rooms, bedrooms, and workplaces",
    ],
  },
  {
    first: "ROLLER",
    second: "Blinds",
    slug: "rollerblinds",
    image: rollerImg,
    tagline: "Effortless elegance with smooth functionality.",
    description:
      "Roller blinds are a sleek and practical window covering designed for modern living. Made from a single piece of fabric that rolls up neatly into a tube, they offer a clean, minimal look while providing excellent control over light and privacy.\nWith a simple pull or motorized system, you can easily adjust the level of sunlight entering your space — from soft ambient light to full shade. Roller blinds are perfect for creating a polished, uncluttered aesthetic in any room.\nThey come in a variety of materials, colors, and opacity levels — from light-filtering fabrics to full blackout options — making them ideal for both residential and commercial spaces.",
    features: [
      "Sleek, space-saving design",
      "Available in light-filtering and blackout fabrics",
      "Easy manual or motorized operation",
      "Durable, low-maintenance materials",
      "Enhances privacy and energy efficiency",
      "Perfect for homes, offices, and meeting rooms",
    ],
  },
  {
    first: "VERTICAL",
    second: "Blinds",
    slug: "verticalblinds",
    image: verticalImg,
    tagline: "Refined versatility for light and space control.",
    description:
      "Vertical blinds bring a touch of sophistication and functionality to any interior. Designed with sleek vertical slats that glide effortlessly along a track, they allow you to precisely control light, privacy, and airflow. Perfect for large windows, sliding doors, and office spaces, vertical blinds add a clean, architectural look while remaining practical and easy to maintain.\nWith a simple tilt or draw, you can let in natural light or achieve full coverage for complete privacy. Available in a wide range of fabrics, textures, and colors, they complement both contemporary and classic interiors.",
    features: [
      "Elegant, modern design ideal for large windows and glass doors",
      "Smooth operation with tilt and draw controls",
      "Available in light-filtering and blackout materials",
      "Easy to clean and maintain",
      "Enhances privacy and sun protection",
      "Suitable for homes, offices, and commercial interiors",
    ],
  },
  {
    first: "BAMBOO",
    second: "Blinds",
    slug: "bambooblinds",
    image: bambooImg,
    tagline: "Natural textures with eco-friendly charm.",
    description:
      "Bamboo blinds bring warmth and an organic feel to any space, combining sustainability with timeless design. Crafted from carefully selected natural bamboo, these blinds create a soft, diffused lighting effect that enhances comfort and relaxation. Their earthy tones and textures blend beautifully with modern, rustic, and tropical interiors. In addition to aesthetics, they provide practical light control while maintaining airflow and brightness. Bamboo blinds are an excellent choice for those who appreciate eco-conscious living without compromising on style and functionality.",
    features: [
      "Made from eco-friendly, natural bamboo materials",
      "Soft light filtering with a warm natural glow",
      "Lightweight and easy to operate",
      "Adds texture and character to interiors",
      "Perfect for living spaces, balconies, and bedrooms",
    ],
  },
  {
    first: "VENETIAN",
    second: "Blinds",
    slug: "venetianblinds",
    image: venetianImg,
    tagline: "Precision light control with classic style.",
    description:
      "Venetian blinds are a versatile window solution designed with horizontal slats that can be easily adjusted to control light and privacy. Their clean and structured look makes them suitable for both modern and traditional interiors. With a simple tilt mechanism, you can let in natural light or block it completely as needed. Available in a variety of materials such as aluminum, wood, and PVC, they offer flexibility in both style and durability. These blinds are especially popular in workspaces and homes where functionality and neat aesthetics are equally important.",
    features: [
      "Adjustable slats for accurate light control",
      "Available in metal, wood, and PVC finishes",
      "Easy to clean and maintain",
      "Durable and long-lasting design",
      "Ideal for offices, kitchens, and living areas",
    ],
  },
  {
    first: "ROMAN",
    second: "Blinds",
    slug: "romanblinds",
    image: romanImg,
    tagline: "Soft elegance with luxurious folds.",
    description:
      "Roman blinds offer a perfect balance between comfort and sophistication, combining the softness of curtains with the practicality of blinds. When raised, they form smooth, elegant folds that create a refined and luxurious appearance. These blinds are available in a wide range of fabrics, patterns, and colors to match any décor style. They not only enhance the visual appeal of a room but also provide excellent light filtering and insulation. Roman blinds are ideal for creating a cozy and stylish atmosphere in both modern and classic interiors.",
    features: [
      "Elegant fabric folds for a premium look",
      "Wide range of fabrics and colors",
      "Provides excellent light filtering and insulation",
      "Smooth and easy operation",
      "Perfect for bedrooms, lounges, and formal spaces",
    ],
  },
];

const Blinds = () => {
  const { t } = useTranslation();

  return (
    <div className="flex flex-column mb-6">
      <div
        className="bg-top bg-no-repeat flex flex-column align-items-center pt-7 page-image-header"
        style={{ backgroundImage: `url(${galleryImage})` }}
      >
        <h1 className="text-primary cormorant-garamond font-bold text-10xl">
          {t("Blinds")}
        </h1>
        <h1 className="lg:text-8xl text-6xl lg:mt-5 mt-2 text-elegance">
          {t("Soft & Sleek")}
        </h1>
        <p className="text-primary lg:text-2xl text-lg text-center lg:mt-6 mt-4 gallery-p">
          {t(
            "Explore our wide range of the latest curtain and blind designs that meet all your needs at the most affordable prices"
          )}
        </p>
      </div>

      <div className="blinds-sections">
        {blindTypes.map((blind, index) => (
          <section
            key={blind.slug}
            className={`blinds-section ${index % 2 === 0 ? "bg-cream" : "bg-stone"}`}
          >
            <div className="blinds-section-inner">
              <div className="blinds-info-col">
                <h2 className="blinds-heading">
                  <span className="blinds-heading-first">{t(blind.first)}</span>{" "}
                  <span className="blinds-heading-second">{t(blind.second)}</span>
                </h2>
                <p className="blinds-tagline">{t(blind.tagline)}</p>
                <div className="blinds-description">
                  {blind.description.split("\n").map((para, i) => (
                    <p key={i}>{t(para)}</p>
                  ))}
                  {blind.bullets && (
                    <ul className="blinds-bullets">
                      {blind.bullets.map((b, i) => (
                        <li key={i}>{t(b)}</li>
                      ))}
                    </ul>
                  )}
                  {blind.extra && <p>{t(blind.extra)}</p>}
                  <p className="blinds-key-features-label">{t("Key Features:")}</p>
                  <ul className="blinds-features">
                    {blind.features.map((f, i) => (
                      <li key={i}>{t(f)}</li>
                    ))}
                  </ul>
                </div>
                <Link
                  to={`/gallery/${blind.slug}`}
                  className="blinds-cta-button"
                >
                  {t("Click to see price & Designs")} &nbsp;&rsaquo;
                </Link>
              </div>
              <div className="blinds-image-col">
                <img src={blind.image} alt={`${blind.first} ${blind.second}`} />
              </div>
            </div>
          </section>
        ))}
      </div>
    </div>
  );
};

export default Blinds;
